import { Injectable, inject } from '@angular/core';
import FetchingService from '../services/fetching.service';
import RestService from '../services/core-rest.service';
import Config from '../utils/config.service';
import AngularHelperService from '../services/angular-helper.service';
import AngularBroadcastHelperService from '../services/angular-broadcast-helper.service';
import moment from 'moment';

@Injectable({ providedIn: 'root' })
export default class UserService {
	private fetchingService: FetchingService = inject(FetchingService);
	private restService: RestService = inject(RestService);
	private config: Config = inject(Config);
	private angularHelperService: AngularHelperService = inject(AngularHelperService);
	private angularBroadcastHelperService: AngularBroadcastHelperService = inject(AngularBroadcastHelperService);
	
	store: any = {
		wallet: null,
		userEhifData: null,
		userEhifPrescriptions: null,
		thirdPersonWallets: null
	};
	
	constructor() {
		this.angularBroadcastHelperService.onRootEvent(this.config.getBroadcastConfig().Session.LOGOUT, (event) => {
			this.store.userEhifPrescriptions = null;
		});
	}
	
	getWallet() {
		if (!this.store.wallet) this.fetchWallet();
		return this.store.wallet;
	}
	
	clearWalletCache() {
		this.fetchingService.clear('USER_WALLET');
		this.store.wallet = null;
		this.store.thirdPersonWallets = null;
		this.angularBroadcastHelperService.rootBroadcast("walletChanged", this.store.wallet);
	}
	
	fetchWallet() {
		var deferred: any = this.angularHelperService.getDeferredPromise();
		
		this.fetchingService.get('USER_WALLET', { threshold: 300 })
			.then((response) => {
				this.store.wallet = response;
				this.angularBroadcastHelperService.rootBroadcast("walletChanged", this.store.wallet);
				deferred.resolve(response);
			})
			.catch((e) => {
				console.error(e);
				deferred.reject();
			});
		
		return deferred.promise;
	}
	
	getWalletWithPrice(service) {
		var deferred: any = this.angularHelperService.getDeferredPromise();
		
		this.fetchWallet().then((wallet) => {
			if (service.careTakenPersonalCode) {
				// fetch third person wallets
				this.getThirdPersonWallet(service.careTakenPersonalCode)
				.then((response) => {
					if (response && response.length > 0) {
						var usedWallet;
						for (var i = 0; i < response.length; i++) {
							var usedBusinessWallet: any = this.getBusinessWalletPrice(service, service.pricing.price, response[i], true);
							
							if (usedBusinessWallet) {
								usedWallet = usedBusinessWallet;
								usedWallet.alternative = true;
								break;
							}
						}
						if (usedWallet) {
							deferred.resolve(usedWallet);
						} else {
							this.getWalletWithPriceInternal(deferred, service, wallet);
						}
					} else {
						this.getWalletWithPriceInternal(deferred, service, wallet);
					}
				})
				.catch((error) => {
					this.getWalletWithPriceInternal(deferred, service, wallet);
				});
			} else {
				this.getWalletWithPriceInternal(deferred, service, wallet);
			}
		}).catch((e) => {
			console.error(e);
			deferred.reject();
		});
		
		return deferred.promise;
	}
	
	getThirdPersonWallet(careTakenPersonalCode) {
		var deferred;
		
		if (this.store.thirdPersonWallets) {
			if (this.store.thirdPersonWallets.personalCode == careTakenPersonalCode) {
				if (this.store.thirdPersonWallets.updatedAt) {
					var secondsSinceLastFetch: any = moment.duration(moment().diff(moment(this.store.thirdPersonWallets.updatedAt))).asSeconds();
					if (secondsSinceLastFetch < 60) {
						//console.log("Return cached third person wallet");
						deferred = this.angularHelperService.getDeferredPromise();
						deferred.resolve(this.store.thirdPersonWallets.wallets);
						return deferred.promise;
					} else {
						//console.log("Re-fetch third person wallet. 60 seconds passed since last fetch.");
						this.initThirdPersonWalletsForFetching(careTakenPersonalCode);
					}
				} else {
					//console.log("Return promise for third person wallet");
					return this.store.thirdPersonWallets.deferred.promise;
				}
			} else {
				//console.log("Personal code changed. Re-fetch third person wallet");
				this.initThirdPersonWalletsForFetching(careTakenPersonalCode);
			}
		} else {
			//console.log("Initial fetch for third person wallet");
			this.initThirdPersonWalletsForFetching(careTakenPersonalCode);
		}
		
		this.restService.getWalletForThirdPerson({ personalCode: careTakenPersonalCode })
			.then((response) => {
				this.store.thirdPersonWallets.wallets = response;
				this.store.thirdPersonWallets.updatedAt = new Date();
				this.store.thirdPersonWallets.deferred.resolve(response);
			})
			.catch((error) => {
				console.error(error);
				this.store.thirdPersonWallets.deferred.resolve([]);
			});
		
		return this.store.thirdPersonWallets.deferred.promise;
	}
	
	initThirdPersonWalletsForFetching(careTakenPersonalCode) {
		var thirdPersonWallets: any = {};
		thirdPersonWallets.personalCode = careTakenPersonalCode;
		this.store.thirdPersonWallets = thirdPersonWallets;
		this.store.thirdPersonWallets.deferred = this.angularHelperService.getDeferredPromise();
	}
	
	getWalletWithPriceInternal(deferred, service, wallet) {
		var businessWallet: any = wallet.businessWallet;
		var price: any = service.pricing.price;
		var alternativeWallets: any = wallet.alternatives;
		var usedWallet: any = {};
		
		if (businessWallet) {
			var usedBusinessWallet: any = this.getBusinessWalletPrice(service, price, businessWallet, false);
			if (usedBusinessWallet) {
				usedWallet = usedBusinessWallet;
			}
		}
		
		if (!usedWallet.wallet && alternativeWallets) {
			var coPayWallet;
			for (var i = 0; i < alternativeWallets.length; i++) {
				var usedBusinessWallet: any = this.getBusinessWalletPrice(service, price, alternativeWallets[i], true);
				if (service.careTakenPersonalCode && alternativeWallets[i].disabledForThirdPerson) {
					usedWallet.insuranceWalletDisabledForThirdPerson = true;
					continue;
				}
				if (service.scheduledStart && alternativeWallets[i].allowedOnlyDuringValidPeriod) {
					if (alternativeWallets[i].validFrom && new Date(alternativeWallets[i].validFrom).getTime() > new Date(service.scheduledStart).getTime()) {
						usedWallet.insuranceWalletDisabledForInvalidPeriod = true;
						continue; // wallet not allowed before valid period has started
					}
					if (alternativeWallets[i].validTo && new Date(service.scheduledStart).getTime() > new Date(alternativeWallets[i].validTo).getTime()) {
						usedWallet.insuranceWalletDisabledForInvalidPeriod = true;
						continue; // wallet not allowed after valid period has ended
					}
				}
				
				if (usedBusinessWallet) {
					if (usedBusinessWallet.wallet.coPay) {
						// set as co-pay wallet only if it is better, than existing or there is no existing
						if (!coPayWallet || coPayWallet.wallet.coPay > usedBusinessWallet.wallet.coPay) {
							coPayWallet = usedBusinessWallet;
						}
					} else {
						usedWallet = usedBusinessWallet;
						usedWallet.alternative = true;
						break;
					}
				}
			}
			if (coPayWallet && !usedWallet.wallet) {
				usedWallet.coPayWallet = coPayWallet;
				usedWallet.alternative = true;
			}
		}
		
		if (!usedWallet.wallet) {
			usedWallet.price = price;
			usedWallet.wallet = wallet;
		}
		
		deferred.resolve(usedWallet);
	}
	
	getBusinessWalletPrice(service, price, businessWallet, alternative) {
		var isEmployerLimitExceeded: any = !alternative && ((price + businessWallet.limits.businessTotalCreditUsed) > businessWallet.limits.businessTotalCreditAvailable);
		var walletRequiresOnDemandSpecialCheck: any = service.generalPractitionerSpecialCheck
		&& businessWallet.allowedFields
		&& businessWallet.allowedFields.filter(function(item) {
			return item === 'ONDEMAND_PRESCRIPTIONS_ONLY' || item === 'ONDEMAND_GP_ONLY';
		})[0] || false;
		var isSessionPriceCapReached: boolean = businessWallet.sessionPriceCap ? businessWallet.sessionPriceCap < price : false;
		
		var isCoveredByBusinessWallet: any = false;
		if (walletRequiresOnDemandSpecialCheck) {
			var walletHasOnDemandPrescriptions: any = service.generalPractitionerSpecialCheck == 'prescriptionsOnly' && businessWallet.allowedFields && businessWallet.allowedFields.filter((item) => {
				return item === 'ONDEMAND_PRESCRIPTIONS_ONLY';
			})[0] || false;
			if (walletHasOnDemandPrescriptions) {
				isCoveredByBusinessWallet = true;
			}
			var walletHasOnDemandGpOnly: any = service.generalPractitionerSpecialCheck == 'gpOnly' && businessWallet.allowedFields && businessWallet.allowedFields.filter((item) => {
				return item === 'ONDEMAND_GP_ONLY';
			})[0] || false;
			if (walletHasOnDemandGpOnly) {
				isCoveredByBusinessWallet = true;
			}
		}
		
		if (!walletRequiresOnDemandSpecialCheck && !isCoveredByBusinessWallet) {
			isCoveredByBusinessWallet = businessWallet.allowedFields && businessWallet.allowedFields.filter((field) => {
				if (Array.isArray(service.furtherSubcategory)) {
					if (service.furtherSubcategory.length == 2) {
						// mental check on-demand service is a "special" case
						if (service.furtherSubcategory[0] === 'MENTAL_CHECK' || service.furtherSubcategory[1] === 'MENTAL_CHECK') {
							return this.matchesSpecialCategory(field, 'MENTAL_CHECK');
						} else {
							return service.furtherSubcategory.filter((category) => { return field === category || this.matchesSpecialCategory(field, category); }).length;
						}
					} else {
						return service.furtherSubcategory.filter((category) => { return field === category || this.matchesSpecialCategory(field, category); }).length;
					}
				} else {
					return field === service.furtherSubcategory || this.matchesSpecialCategory(field, service.furtherSubcategory);
				}
			}).length;
		}
		
		var priceForWallet: any = isCoveredByBusinessWallet
			&& businessWallet.balance >= price
			&& !isEmployerLimitExceeded
			&& !isSessionPriceCapReached ? 0 : price;
		
		if (priceForWallet == 0) {
			var walletUsed: any = {};
			walletUsed.price = 0;
			walletUsed.wallet = businessWallet;
			return walletUsed;
		} else {
			return null;
		}
	}
	
	matchesSpecialCategory(field, category) {
		return "MENTAL_CHECK" === category && ("PSYCHOLOGIST" === field || "CLINICAL_PSYCHOLOGIST" === field);
	}
	
	getUserInsuranceData() {
		var deferred: any = this.angularHelperService.getDeferredPromise();
		this.restService.getInsuranceInfo()
			.then((response) => {
				deferred.resolve(response);
			})
			.catch((error) => {
				console.log(error);
				deferred.resolve(error.error);
			});
		return deferred.promise;
	}
	
	getEhifPrescription(prescriptionNumber) {
		if (this.store.userEhifPrescriptions) {
			var filtered: any = this.store.userEhifPrescriptions.filter(function (event) {
				return event.prescriptionNumber === prescriptionNumber;
			});
			return filtered.length > 0 ? filtered[0] : undefined;
		} else {
			return undefined;
		}
	}
	
	setEhifPrescriptions(prescriptions) {
		this.store.userEhifPrescriptions = prescriptions;
	}
	
	clearEhifData() {
		this.store.userEhifPrescriptions = null;
	}
	
}
